import { supabase } from '@/integrations/supabase/client';
import type { EventType } from './eventCatalog';
import { missingRequiredKeys, type EventPayload, type ContractedEvent } from './eventPayloadContracts';

type MetaArgs<E extends EventType> = E extends ContractedEvent
  ? [metadata: EventPayload<E>]
  : [metadata?: Record<string, unknown>];

/** Refuse (loudly in dev) rather than write a row that breaks C-AN-18. */
function violatesContract(eventType: string, metadata: Record<string, unknown> | undefined): boolean {
  const missing = missingRequiredKeys(eventType, metadata);
  if (missing.length === 0) return false;
  console.error(`[trackActivity] dropped ${eventType}: missing required metadata ${missing.join(', ')}`);
  return true;
}

/**
 * Fire-and-forget activity event logger.
 * `eventType` MUST be declared in `src/lib/eventCatalog.ts` —
 * unknown events are blocked at compile time.
 *
 * Never throws, never blocks UI.
 */
export function trackActivity<E extends EventType>(
  eventType: E,
  ...[metadata]: MetaArgs<E>
) {
  if (violatesContract(eventType, metadata as Record<string, unknown> | undefined)) return;
  try {
    supabase.auth.getUser().then(({ data }) => {
      const userId = data.user?.id;
      if (!userId) return;
      supabase
        .from('user_activity_log' as any)
        .insert({
          user_id: userId,
          event_type: eventType,
          event_metadata: metadata ?? {},
        })
        .then(({ error }) => {
          if (error) console.debug('[trackActivity] insert error (non-fatal):', error.message);
        });
    }).catch(() => {});
  } catch {
    // absolute safety net
  }
}

/**
 * Same as trackActivity but when you already have the userId.
 * Avoids an extra auth call.
 */
export function trackActivityForUser<E extends EventType>(
  userId: string,
  eventType: E,
  ...[metadata]: MetaArgs<E>
) {
  if (violatesContract(eventType, metadata as Record<string, unknown> | undefined)) return;
  try {
    supabase
      .from('user_activity_log' as any)
      .insert({
        user_id: userId,
        event_type: eventType,
        event_metadata: metadata ?? {},
      })
      .then(({ error }) => {
        if (error) console.debug('[trackActivity] insert error (non-fatal):', error.message);
      });
  } catch {
    // absolute safety net
  }
}
